import { useState, useEffect, useCallback, useRef, createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PaperWidth } from "@/lib/thermal-printer";
import { createAsyncMutex, type AsyncMutex } from "@/lib/async-mutex";
import { reportError } from "@/components/ErrorBoundary";
import {
  findCurrentOwner as findPrinterOwner,
  markOwner as markPrinterOwner,
  markReleased as markPrinterReleased,
} from "@/lib/printer-tab-coordinator";
import { customFetch } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import React from "react";

const PRINTER_OP_TIMEOUT_MS = 30_000;
const PRINTER_DEBUG = !!import.meta.env.DEV;

// VIDs USB de chips genéricos chineses cuja ROM não tem glyphs Latin-1 —
// acento sai como lixo em qualquer codepage ESC/POS. Pra essas, callers usam
// `formatReceiptBytes(..., asciiOnly=true)` que transliterá o cupom pra
// ASCII puro. Lista espelha `KNOWN_VIDS` no Print Agent (cheapPrinterSetup.ts).
const KNOWN_ASCII_ONLY_VIDS = new Set<number>([
  0x28E9, // YICHIP — térmicas baratas (~R$ 90) do Mercado Livre/Shopee
]);

function planAllowsPrinter(_plan: string | undefined): boolean {
  return true;
}

const STORAGE_KEY = "vnz-thermal-printer";

interface PrinterPrefs {
  autoPrintNewOrder: boolean;
  autoPrintStatusChange: boolean;
  autoPrintStatusList: string[];
  paperWidth: PaperWidth;
}

export interface PrintQueueItem {
  id: string;
  orderId: string;
  reason: string;
  createdAt: string;
  /** Os bytes que o servidor montou no claim (ESC/POS em base64). */
  payload?: { mode?: string; bytes?: string } | null;
}

// Papéis que o servidor monta no claim (29/10/2026): o item da fila leva um pedido, mas o que sai é o aviso «A mesa 6
// pediu a conta», a conta completa ou os QR codes das mesas. Montar aqui pelo pedido imprimiria o cupom dele. Motivos:
// conta_pedida, conta_pedida_completa, conta, conta_cliente e qrcodes:1-10.
const ehPapelDeConta = (motivo: string | null | undefined) => /^(conta(_|$)|qrcodes:)/.test(motivo ?? "");

function bytesDoServidor(payload: PrintQueueItem["payload"]): Uint8Array | null {
  if (!payload?.bytes) return null;
  const bin = atob(payload.bytes);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// "queued" só quando o servidor pôs o pedido na fila. Sem fila (conta dev, POST falhou, `skipped`), "failed".
export type PrintResult = "printed" | "queued" | "failed";

function loadPrefs(): PrinterPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaultPrefs(), ...JSON.parse(raw) };
  } catch {}
  return defaultPrefs();
}

function defaultPrefs(): PrinterPrefs {
  return { autoPrintNewOrder: true, autoPrintStatusChange: false, autoPrintStatusList: ["confirmado"], paperWidth: "80mm" };
}

function savePrefs(p: PrinterPrefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
}

// Repete os bytes do cupom N vezes (N vias). Cada cupom já termina com corte,
// então saem N cupons separados num único job.
function repeatBytes(data: Uint8Array, n: number): Uint8Array {
  if (n <= 1) return data;
  const out = new Uint8Array(data.length * n);
  for (let i = 0; i < n; i++) out.set(data, i * data.length);
  return out;
}

export function isWebUsbSupported(): boolean {
  return "usb" in navigator;
}

const THERMAL_PRINTER_FILTERS = [{ classCode: 7 }];

async function findUsbEndpoint(device: USBDevice): Promise<{ iface: USBInterface; endpoint: USBEndpoint } | null> {
  for (const config of device.configurations) {
    for (const iface of config.interfaces) {
      for (const alt of iface.alternates) {
        if (alt.interfaceClass === 7 || alt.interfaceClass === 0xFF) {
          const ep = alt.endpoints.find(e => e.direction === "out" && e.type === "bulk");
          if (ep) return { iface, endpoint: ep };
        }
      }
    }
  }
  for (const config of device.configurations) {
    for (const iface of config.interfaces) {
      for (const alt of iface.alternates) {
        const ep = alt.endpoints.find(e => e.direction === "out" && e.type === "bulk");
        if (ep) return { iface, endpoint: ep };
      }
    }
  }
  return null;
}

const PRINT_INTERVAL_MS = 1000;
const QUEUE_POLL_MS = 10000;

interface ThermalPrinterState {
  supported: boolean;
  connected: boolean;
  /** A loja já ligou impressora alguma vez (térmica ou app; `printerConnectedAt`). Sem isso a fila não tem quem
   *  imprima, e o enqueueOrder nem manda o pedido. */
  printerEverConnected: boolean;
  connecting: boolean;
  prefs: PrinterPrefs;
  updatePrefs: (partial: Partial<PrinterPrefs>) => void;
  connect: () => Promise<{ ok: boolean; error?: string }>;
  disconnect: () => Promise<void>;
  printData: (data: Uint8Array, orderId?: string, reason?: string) => Promise<PrintResult>;
  queue: PrintQueueItem[];
  enqueueOrder: (orderId: string, reason?: string) => Promise<boolean>;
  clearQueue: () => Promise<void>;
  flushQueue: () => void;
  processingQueue: boolean;
  printedOrderIds: Set<string>;
  refreshQueue: () => Promise<void>;
  generateReceiptData: ((orderId: string) => Uint8Array | null) | null;
  setGenerateReceiptData: (fn: (orderId: string) => Uint8Array | null) => void;
  /** True quando o VID USB da impressora conectada está na lista
   *  KNOWN_ASCII_ONLY_VIDS (térmica barata sem glyphs Latin-1 na ROM).
   *  Callers devem usar `formatReceiptBytes(..., asciiOnly=true)` pra
   *  transliterar acentos antes de mandar pra impressora. False quando
   *  desconectada ou quando o VID não está na lista. */
  isAsciiOnlyPrinter: boolean;
}

const ThermalPrinterContext = createContext<ThermalPrinterState | null>(null);

export function ThermalPrinterProvider({ children }: { children: ReactNode }) {
  const value = useThermalPrinterInternal();
  return React.createElement(ThermalPrinterContext.Provider, { value }, children);
}

export function useThermalPrinter(): ThermalPrinterState {
  const ctx = useContext(ThermalPrinterContext);
  if (!ctx) throw new Error("useThermalPrinter must be used inside ThermalPrinterProvider");
  return ctx;
}

function useThermalPrinterInternal(): ThermalPrinterState {
  const { user: authUser } = useAuth();
  // O provedor envolve TODAS as rotas. Sem login (cardápio, checkout, agendamento, Link do PDV) a fila, o estado da
  // impressora e as configurações só dão 401: cada visitante gerava um a cada 10 s (até 11 mil por hora, 19/10/2026).
  // O Link do PDV imprime pelo /api/pdv/:slug e não depende deles.
  const logado = !!authUser;
  const isDev = (authUser as any)?.user?.role === "dev";
  const isDevRef = useRef(isDev);
  useEffect(() => { isDevRef.current = isDev; }, [isDev]);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [prefs, setPrefsState] = useState<PrinterPrefs>(loadPrefs);
  const [queue, setQueue] = useState<PrintQueueItem[]>([]);
  const [printedOrderIds, setPrintedOrderIds] = useState<Set<string>>(new Set());
  const [processingQueue, setProcessingQueue] = useState(false);
  const [vendorId, setVendorId] = useState<number | null>(null);

  const usbDeviceRef = useRef<USBDevice | null>(null);
  const usbEndpointRef = useRef<number | null>(null);
  const processingRef = useRef(false);
  const connectedRef = useRef(false);
  const drainRequestedRef = useRef(false);
  const generateReceiptRef = useRef<((orderId: string) => Uint8Array | null) | null>(null);

  // Mutex que serializa todas as operações que tocam o USBDevice dentro
  // desta aba (transferOut, close, releaseInterface, open+claim). Resolve
  // a race entre printData direto e drainQueue (e variantes), que antes
  // podiam chamar transferOut em paralelo na mesma porta. Não cobre
  // múltiplas abas — WebUSB não tem lock cross-tab confiável.
  const usbMutexRef = useRef<AsyncMutex | null>(null);
  if (usbMutexRef.current === null) {
    usbMutexRef.current = createAsyncMutex({
      timeoutMs: PRINTER_OP_TIMEOUT_MS,
      onTimeout: (label, ms) => {
        try {
          reportError(
            new Error(`[printer-mutex-timeout] op=${label} held lock >${ms}ms`),
            null,
          );
        } catch {}
      },
      debug: PRINTER_DEBUG
        ? (event, label, ms) => {
            // eslint-disable-next-line no-console
            console.debug(`[printer-mutex] ${event} op=${label}${ms ? ` ms=${ms}` : ""}`);
          }
        : undefined,
    });
  }
  const runUsb = useCallback(<T,>(label: string, fn: () => Promise<T>): Promise<T> => {
    return usbMutexRef.current!.runExclusive(fn, { label });
  }, []);

  const supported = isWebUsbSupported();

  type PrinterSettingsData = {
    plan?: string;
    printCopies?: number;
    pdvSettings?: {
      autoPrintNewOrder?: boolean;
      autoPrintStatusChange?: boolean;
      autoPrintStatusList?: string[];
    } | null;
  };
  const { data: settingsData } = useQuery<PrinterSettingsData | null>({
    queryKey: ["settings"],
    queryFn: () => customFetch<PrinterSettingsData>("/api/settings").catch(() => null),
    staleTime: 30_000,
    enabled: logado,
  });
  const printerLocked = !planAllowsPrinter(settingsData?.plan);
  const printerLockedRef = useRef(printerLocked);
  useEffect(() => { printerLockedRef.current = printerLocked; }, [printerLocked]);

  // Vias de impressão (1..10): impressão no navegador repete os bytes N vezes
  // (o agente repete no servidor, em orderPayload). Ref pra usar dentro do drain.
  const printCopiesRef = useRef(1);
  useEffect(() => {
    const n = Math.trunc(Number(settingsData?.printCopies));
    printCopiesRef.current = !Number.isFinite(n) || n < 1 ? 1 : Math.min(n, 10);
  }, [settingsData?.printCopies]);

  const { data: printerStatus, refetch: refetchPrinterStatus } = useQuery<{ printerConnectedAt: string | null } | null>({
    queryKey: ["printer-status"],
    queryFn: () => customFetch<{ printerConnectedAt: string | null }>("/api/print-queue/printer-status").catch(() => null),
    staleTime: 60_000,
    enabled: logado,
  });
  const printerEverConnected = !!printerStatus?.printerConnectedAt;
  const printerEverConnectedRef = useRef(printerEverConnected);
  useEffect(() => { printerEverConnectedRef.current = printerEverConnected; }, [printerEverConnected]);

  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);

  const updatePrefs = useCallback((partial: Partial<PrinterPrefs>) => {
    setPrefsState(prev => {
      const next = { ...prev, ...partial };
      savePrefs(next);
      return next;
    });
  }, []);

  // Versão "interna" do disconnect: NÃO adquire o mutex. Usar somente
  // de dentro de uma seção crítica já em curso (ex.: sendRaw em path de
  // erro), pra evitar deadlock auto-reentrante.
  const disconnectInner = useCallback(async (markUserDisconnected: boolean) => {
    try {
      if (usbDeviceRef.current) {
        await usbDeviceRef.current.close().catch(() => {});
      }
    } catch {}
    usbDeviceRef.current = null;
    usbEndpointRef.current = null;
    setConnected(false);
    setVendorId(null);
    markPrinterReleased();
    if (markUserDisconnected) {
      try { localStorage.setItem("printer:userDisconnected", "1"); } catch {}
    }
  }, []);

  // Versão pública: serializa o close() pelo mutex. Usar de handlers
  // externos (botão "Desconectar", reconnect, etc.).
  const disconnect = useCallback(async () => {
    await runUsb("disconnect", () => disconnectInner(true));
  }, [runUsb, disconnectInner]);

  const sendRaw = useCallback(async (data: Uint8Array): Promise<{ ok: boolean; partial: boolean }> => {
    try {
      return await runUsb("sendRaw", async () => {
        if (!usbDeviceRef.current || usbEndpointRef.current === null) return { ok: false, partial: false };
        let anyChunkSent = false;
        try {
          const CHUNK = 64;
          for (let i = 0; i < data.length; i += CHUNK) {
            const chunk = data.slice(i, i + CHUNK);
            await usbDeviceRef.current.transferOut(usbEndpointRef.current, chunk);
            anyChunkSent = true;
          }
          return { ok: true, partial: false };
        } catch (e) {
          console.error("USB print failed:", e);
          // Já estamos dentro do mutex — usar disconnectInner pra não tentar
          // re-adquirir e travar.
          await disconnectInner(false);
          return { ok: false, partial: anyChunkSent };
        }
      });
    } catch (e: any) {
      // runUsb pode rejeitar com timeout ou erro inesperado. Traduz para
      // o contrato esperado pelos callers ({ ok, partial }) pra não
      // quebrar drainQueue/printData/connect.
      //
      // IMPORTANTE: em caso de timeout do mutex, a operação de
      // transferOut original ainda PODE estar rodando em background (o
      // mutex segura o lock até ela terminar de verdade, mas só pra
      // evitar concorrência — não consegue cancelar). Então se a
      // impressão eventualmente completar, marcar partial=false faria
      // drainQueue re-enfileirar e duplicar o cupom. Marcamos partial=true
      // pra ficar do lado seguro: não duplica, no pior caso o usuário
      // reimprime manualmente se nada saiu.
      const isTimeout = e?.name === "AsyncMutexTimeoutError";
      console.error("sendRaw rejected by mutex:", e);
      return { ok: false, partial: isTimeout };
    }
  }, [runUsb, disconnectInner]);

  const refreshQueue = useCallback(async () => {
    try {
      const resp = await customFetch<{ items: PrintQueueItem[] }>("/api/print-queue");
      const items = resp?.items;
      setQueue(Array.isArray(items) ? items : []);
    } catch (e) {
      console.error("Failed to fetch print queue:", e);
    }
  }, []);

  const removeFromQueue = useCallback(async (entryId: string) => {
    try {
      await customFetch("/api/print-queue/" + entryId, { method: "DELETE" });
      setQueue(prev => prev.filter(q => q.id !== entryId));
    } catch (e) {
      console.error("Failed to remove from print queue:", e);
    }
  }, []);

  const claimQueueItem = useCallback(async (entryId: string): Promise<PrintQueueItem | null> => {
    try {
      const resp = await customFetch<{ claimed: boolean; item?: PrintQueueItem; payload?: PrintQueueItem["payload"] }>(
        "/api/print-queue/" + entryId + "/claim",
        { method: "POST" },
      );
      setQueue(prev => prev.filter(q => q.id !== entryId));
      if (resp?.claimed && resp.item) return { ...resp.item, payload: resp.payload ?? null };
      return null;
    } catch (e: any) {
      const status = e?.status || e?.response?.status;
      if (status === 404) {
        setQueue(prev => prev.filter(q => q.id !== entryId));
        return null;
      }
      console.error("Failed to claim print queue item:", e);
      throw e;
    }
  }, []);

  const drainQueue = useCallback(async () => {
    if (isDevRef.current) return;
    if (printerLockedRef.current) return;
    if (processingRef.current) {
      drainRequestedRef.current = true;
      return;
    }
    processingRef.current = true;
    setProcessingQueue(true);

    try {
    let keepGoing = true;
    while (keepGoing) {
      drainRequestedRef.current = false;

      const freshItemsRaw = await customFetch<{ items: PrintQueueItem[] }>("/api/print-queue")
        .then(r => r.items)
        .catch(() => [] as PrintQueueItem[]);
      const freshItems = Array.isArray(freshItemsRaw) ? freshItemsRaw : [];
      setQueue(freshItems);

      if (freshItems.length === 0) break;

      for (let i = 0; i < freshItems.length; i++) {
        if (!connectedRef.current || printerLockedRef.current) { keepGoing = false; break; }

        const item = freshItems[i];
        const gen = generateReceiptRef.current;
        if (!gen) { keepGoing = false; break; }

        let claimed: PrintQueueItem | null;
        try {
          claimed = await claimQueueItem(item.id);
        } catch {
          try {
            // retry:true = recria SÓ a via ANY que este browser consumiu — sem
            // fan-out (recriaria vias de aparelhos que já imprimiram/ackaram).
            await customFetch("/api/print-queue", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ orderId: item.orderId, reason: item.reason || "new_order", retry: true }),
            });
          } catch (e) {
            console.error("Failed to recover after ambiguous claim:", e);
          }
          keepGoing = false;
          break;
        }
        if (!claimed) continue;

        const aviso = ehPapelDeConta(claimed.reason);
        const data = aviso ? bytesDoServidor(claimed.payload) : gen(claimed.orderId);
        if (!data) continue;

        const sendResult = await sendRaw(aviso ? data : repeatBytes(data, printCopiesRef.current));
        if (sendResult.ok) {
          if (!aviso) setPrintedOrderIds(prev => {
            const next = new Set(prev);
            next.add(claimed!.orderId);
            return next;
          });
        } else {
          if (sendResult.partial) {
            // O cupom pode ter sido impresso parcialmente / totalmente antes da falha.
            // Não reenfileira automaticamente para evitar duplicação.
            setPrintedOrderIds(prev => {
              const next = new Set(prev);
              next.add(claimed!.orderId);
              return next;
            });
          } else {
            try {
              // retry:true — ver comentário no recover acima (sem fan-out).
              await customFetch("/api/print-queue", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ orderId: claimed.orderId, reason: claimed.reason || "retry", retry: true }),
              });
            } catch (e) {
              console.error("Failed to re-enqueue after print failure:", e);
            }
          }
          keepGoing = false;
          break;
        }

        if (i < freshItems.length - 1) {
          await new Promise(r => setTimeout(r, PRINT_INTERVAL_MS));
        }
      }

      if (keepGoing) {
        const remainingRaw = await customFetch<{ items: PrintQueueItem[] }>("/api/print-queue")
          .then(r => r.items)
          .catch(() => [] as PrintQueueItem[]);
        const remaining = Array.isArray(remainingRaw) ? remainingRaw : [];
        setQueue(remaining);
        if (remaining.length === 0) break;
      }
    }
    } finally {
      // Sempre limpa flags, mesmo se algo lançar (ex.: timeout do mutex,
      // claimQueueItem rejeitando inesperadamente). Senão a fila trava
      // pra sempre porque drainQueue cai cedo no `if (processingRef.current)`.
      processingRef.current = false;
      setProcessingQueue(false);
    }

    if (drainRequestedRef.current) {
      drainRequestedRef.current = false;
      setTimeout(() => drainQueue(), 100);
    }
  }, [sendRaw, claimQueueItem]);

  const enqueueOrder = useCallback(async (orderId: string, reason?: string): Promise<boolean> => {
    if (isDevRef.current) return false;
    if (printerLockedRef.current) return false;
    if (!printerEverConnectedRef.current) return false;
    try {
      const resp = await customFetch<{ skipped?: string }>("/api/print-queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId, reason: reason || "new_order" }),
      });
      // 200 com `skipped` (para o servidor, a loja nunca ligou impressora): nada entrou na fila.
      if (resp?.skipped) return false;
      await refreshQueue();
      return true;
    } catch (e) {
      console.error("Failed to enqueue order:", e);
      return false;
    }
  }, [refreshQueue]);

  const clearQueue = useCallback(async () => {
    try {
      await customFetch("/api/print-queue", { method: "DELETE" });
      setQueue([]);
    } catch (e) {
      console.error("Failed to clear print queue:", e);
    }
  }, []);

  const flushQueue = useCallback(() => {
    drainQueue();
  }, [drainQueue]);

  const printData = useCallback(async (data: Uint8Array, orderId?: string, reason?: string): Promise<PrintResult> => {
    if (printerLockedRef.current) return "failed";
    if (!connectedRef.current) {
      if (orderId) return (await enqueueOrder(orderId, reason)) ? "queued" : "failed";
      return "failed";
    }

    if (processingRef.current) {
      if (orderId) return (await enqueueOrder(orderId, reason)) ? "queued" : "failed";
      return "failed";
    }

    let hasQueuedItems = false;
    setQueue(prev => {
      hasQueuedItems = prev.length > 0;
      return prev;
    });
    await new Promise(r => setTimeout(r, 0));

    if (hasQueuedItems && orderId) {
      const entrou = await enqueueOrder(orderId, reason);
      drainQueue();
      return entrou ? "queued" : "failed";
    }

    const sendResult = await sendRaw(data);
    if (sendResult.ok) {
      if (orderId) {
        setPrintedOrderIds(prev => {
          const next = new Set(prev);
          next.add(orderId);
          return next;
        });
      }
      return "printed";
    }

    if (sendResult.partial) {
      // Falha após enviar parte dos dados — o cupom provavelmente já saiu.
      // Não reenfileira para evitar duplicação. Marca como impresso localmente.
      if (orderId) {
        setPrintedOrderIds(prev => {
          const next = new Set(prev);
          next.add(orderId);
          return next;
        });
      }
      return "printed";
    }

    if (orderId) return (await enqueueOrder(orderId, reason)) ? "queued" : "failed";
    return "failed";
  }, [sendRaw, enqueueOrder, drainQueue]);

  const setGenerateReceiptData = useCallback((fn: (orderId: string) => Uint8Array | null) => {
    generateReceiptRef.current = fn;
  }, []);

  const openUsbDevice = useCallback(async (device: USBDevice): Promise<{ ok: boolean; error?: string }> => {
    // Antes de tentar abrir, pergunta às outras abas se alguma já é dona
    // da impressora. Se sim, evita o erro genérico "Unable to claim" e
    // mostra mensagem específica pro lojista fechar a outra aba. Best-effort:
    // se BroadcastChannel não existir ou ninguém responder, segue normal.
    const preOwner = await findPrinterOwner();
    if (preOwner) {
      return {
        ok: false,
        error:
          "Outra aba deste app já está usando a impressora. Feche as outras abas do Venda no Zap e tente de novo.",
      };
    }
    // Bloco open + selectConfiguration + claimInterface deve rodar
    // atomicamente — caso contrário, dois "Conectar" consecutivos ou um
    // hot-plug concorrente podem chamar device.open() em paralelo e
    // bagunçar o estado da porta.
    try {
      return await runUsb("openUsbDevice", async () => {
      try {
        await device.open();
      } catch (e: any) {
        console.error("USB open() failed:", e);
        // Telemetria: manda o erro original (com error.name como
        // "SecurityError"/"NetworkError") pro backend, antes de traduzir
        // pra mensagem amigável. Permite diagnosticar no painel admin
        // /results sem depender do lojista relatar.
        try { reportError(e instanceof Error ? e : new Error(String(e)), null, "thermal-printer/open"); } catch {}
        const name = e?.name || "";
        const msg = (e?.message || String(e)).toLowerCase();
        if (name === "SecurityError" || msg.includes("access denied") || msg.includes("permission")) {
          // Sem Zadig: ele troca o driver da impressora e a tira do Windows — o
          // iFood e o app de impressão param (caso Oxe doceria, 03/10/2026). No
          // Windows o caminho é o app de impressão, que usa a impressora instalada.
          return {
            ok: false,
            error: /Windows/i.test(navigator.userAgent)
              // Mantém o prefixo "Acesso negado": o Settings casa /Acesso negado/
              // pra abrir o diálogo que oferece o app de impressão.
              ? "Acesso negado: o Windows não deixa o navegador usar esta impressora. Use o aplicativo de impressão (Configurações → Impressora Térmica → Baixar instalador)."
              : "Acesso negado à impressora. No Linux, ajuste as permissões udev.",
          };
        }
        if (msg.includes("unable to claim") || msg.includes("already") || msg.includes("busy") || msg.includes("network")) {
          return { ok: false, error: "Impressora em uso por outro programa ou aba. Feche o app de impressão, outras abas do navegador e tente novamente." };
        }
        return { ok: false, error: `Não foi possível abrir a impressora: ${e?.message || e}` };
      }
      try {
        if (device.configuration === null) {
          await device.selectConfiguration(1);
        }
        const result = await findUsbEndpoint(device);
        if (!result) {
          await device.close().catch(() => {});
          return { ok: false, error: "Impressora não tem endpoint compatível (USB classe 7 / bulk out)." };
        }
        try {
          await device.claimInterface(result.iface.interfaceNumber);
        } catch (e: any) {
          console.error("USB claimInterface failed:", e);
          // Telemetria: erro original do navegador (DOMException com name
          // como "NetworkError"/"InvalidStateError") vai pro backend antes
          // de qualquer tradução.
          try { reportError(e instanceof Error ? e : new Error(String(e)), null, "thermal-printer/claim"); } catch {}
          await device.close().catch(() => {});
          const msg = (e?.message || String(e)).toLowerCase();
          if (msg.includes("unable to claim") || msg.includes("already") || msg.includes("busy")) {
            // Diferencia "outra aba do nosso site" de "programa externo do PC"
            // checando o coordinator de novo (pode ter mudado durante a tentativa).
            const owner = await findPrinterOwner();
            if (owner) {
              return {
                ok: false,
                error:
                  "Outra aba deste app já está usando a impressora. Feche as outras abas do Venda no Zap e tente de novo.",
              };
            }
            // Mensagem por plataforma — no Android os culpados/instruções
            // são diferentes (não tem Spooler do Windows; geralmente é
            // outro app querendo a impressora ou modo USB errado).
            const ua = (navigator?.userAgent || "").toLowerCase();
            const isAndroid = ua.includes("android");
            const isMac = ua.includes("mac") && !ua.includes("iphone") && !ua.includes("ipad");
            let busyMsg: string;
            if (isAndroid) {
              busyMsg =
                "Impressora ocupada por outro app do celular. Feche outros apps de impressão (RawBT, ESC/POS, etc.) e tente de novo. Se persistir, desconecte e reconecte o cabo USB OTG.";
            } else if (isMac) {
              busyMsg =
                "Impressora ocupada por outro programa do Mac (driver CUPS ou app do fabricante). Pare a fila no 'Preferências do Sistema → Impressoras' e tente de novo. Se persistir, desligue e religue o cabo USB.";
            } else {
              busyMsg =
                "Impressora ocupada por um programa do Windows (driver, spooler de impressão ou app do fabricante). Pare o serviço 'Spooler de Impressão' ou feche o app de impressão e tente de novo. Se persistir, desligue e religue o cabo USB.";
            }
            return { ok: false, error: busyMsg };
          }
          return { ok: false, error: `Falha ao reservar interface: ${e?.message || e}` };
        }
        usbDeviceRef.current = device;
        usbEndpointRef.current = result.endpoint.endpointNumber;
        setConnected(true);
        setVendorId(device.vendorId);
        markPrinterOwner();
        // Marca no servidor que a impressora foi conectada pela primeira vez.
        // O backend só atualiza se ainda for null, então é idempotente.
        customFetch<{ printerConnectedAt: string | null; firstTime: boolean }>(
          "/api/print-queue/printer-connected",
          { method: "POST" },
        )
          .then((resp) => {
            printerEverConnectedRef.current = !!resp?.printerConnectedAt;
            if (resp?.firstTime) refetchPrinterStatus();
          })
          .catch(() => {});
        return { ok: true };
      } catch (e: any) {
        console.error("USB setup failed:", e);
        usbDeviceRef.current = null;
        usbEndpointRef.current = null;
        try { await device.close(); } catch {}
        return { ok: false, error: e?.message || String(e) };
      }
      });
    } catch (e: any) {
      // Mutex timeout ou outra rejeição: traduz pra contrato esperado.
      console.error("openUsbDevice rejected by mutex:", e);
      return { ok: false, error: "Tempo esgotado tentando abrir a impressora. Tente reconectar." };
    }
  }, [runUsb]);

  // A preferência da LOJA manda; printer.prefs é só fallback pra navegador que
  // ainda não recarregou as configurações. Sem isso, uma linha enfileirada pelo
  // servidor (que decide pela config da loja) ficaria presa na fila num
  // navegador cujo localStorage nunca foi configurado — o default de aparelho
  // novo é autoPrintStatusChange:false.
  const storeAutoPrint = settingsData?.pdvSettings;
  const autoPrintNewOrder = storeAutoPrint?.autoPrintNewOrder ?? prefs.autoPrintNewOrder;
  const autoPrintStatusChange = storeAutoPrint?.autoPrintStatusChange ?? prefs.autoPrintStatusChange;
  const autoPrintStatusList = storeAutoPrint?.autoPrintStatusList ?? prefs.autoPrintStatusList;

  useEffect(() => {
    if (connected && queue.length > 0 && !processingRef.current && !printerLocked) {
      const hasAutoPrintable = queue.some(item => {
        const reason = item.reason || "new_order";
        if (reason === "new_order") return autoPrintNewOrder;
        if (reason.startsWith("status:")) {
          if (!autoPrintStatusChange) return false;
          const status = reason.slice("status:".length);
          return (autoPrintStatusList || []).includes(status);
        }
        return true;
      });
      if (!hasAutoPrintable) return;
      const timer = setTimeout(() => drainQueue(), 500);
      return () => clearTimeout(timer);
    }
  }, [connected, queue, drainQueue, printerLocked, autoPrintNewOrder, autoPrintStatusChange, autoPrintStatusList]);

  useEffect(() => {
    if (!logado) return;
    refreshQueue();
    const interval = setInterval(refreshQueue, QUEUE_POLL_MS);
    return () => clearInterval(interval);
  }, [refreshQueue, logado]);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;

    (async () => {
      try {
        let userDisconnected = false;
        try { userDisconnected = localStorage.getItem("printer:userDisconnected") === "1"; } catch {}
        if (userDisconnected) return;
        const devices = await navigator.usb.getDevices();
        if (devices.length > 0 && !cancelled) {
          setConnecting(true);
          await openUsbDevice(devices[0]);
          if (!cancelled) setConnecting(false);
        }
      } catch {}
    })();

    const handleDisconnect = (e: USBConnectionEvent) => {
      if (e.device === usbDeviceRef.current) {
        usbDeviceRef.current = null;
        usbEndpointRef.current = null;
        setConnected(false);
        markPrinterReleased();
      }
    };

    const handleConnect = async () => {
      if (usbDeviceRef.current) return;
      try {
        const devices = await navigator.usb.getDevices();
        if (devices.length > 0) {
          await openUsbDevice(devices[0]);
        }
      } catch {}
    };

    const releaseDevice = () => {
      const dev = usbDeviceRef.current;
      const ep = usbEndpointRef.current;
      if (!dev) return;
      // releaseInterface() e close() retornam Promises; o try/catch síncrono
      // NÃO captura rejeições. Sem .catch, qualquer erro (incluindo o típico
      // "InvalidStateError: An operation that changes interface state is in
      // progress" ao fechar durante navegação/pagehide) vira unhandled
      // rejection e cai no reporter de erros. Adicionamos .catch em ambos.
      try {
        if (ep != null) {
          for (const config of dev.configurations) {
            for (const iface of config.interfaces) {
              if (iface.claimed) {
                try { dev.releaseInterface(iface.interfaceNumber).catch(() => {}); } catch {}
              }
            }
          }
        }
        try { dev.close().catch(() => {}); } catch {}
      } catch {}
      usbDeviceRef.current = null;
      usbEndpointRef.current = null;
      markPrinterReleased();
    };

    const handlePageHide = () => {
      releaseDevice();
    };

    navigator.usb.addEventListener("disconnect", handleDisconnect);
    navigator.usb.addEventListener("connect", handleConnect as any);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);
    return () => {
      cancelled = true;
      navigator.usb.removeEventListener("disconnect", handleDisconnect);
      navigator.usb.removeEventListener("connect", handleConnect as any);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
    };
  }, [supported, openUsbDevice]);

  const connect = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    if (!supported) return { ok: false, error: "Seu navegador não suporta impressão USB. Use Chrome, Edge ou Opera." };
    if (printerLockedRef.current) return { ok: false, error: "A impressora térmica não está disponível no seu plano." };
    try { localStorage.removeItem("printer:userDisconnected"); } catch {}
    setConnecting(true);
    let device: USBDevice;
    try {
      device = await navigator.usb.requestDevice({
        filters: [
          {},
          { classCode: 7 },
          { vendorId: 0x0416 },
          { vendorId: 0x04b8 },
          { vendorId: 0x0519 },
          { vendorId: 0x0fe6 },
          { vendorId: 0x067b },
          { vendorId: 0x1a86 },
          { vendorId: 0x0483 },
          { vendorId: 0x0dd4 },
          { vendorId: 0x154f },
          { vendorId: 0x1504 },
          { vendorId: 0x0a5f },
          { vendorId: 0x20d1 },
          { vendorId: 0x6868 },
          { vendorId: 0x28e9 },
          { vendorId: 0x1fc9 },
          { vendorId: 0x0471 },
          { vendorId: 0x076b },
          { vendorId: 0x0bda },
        ],
      });
    } catch (e: any) {
      setConnecting(false);
      const msg = (e?.message || String(e)).toLowerCase();
      if (msg.includes("no device selected") || msg.includes("cancelled")) {
        return { ok: false, error: "Nenhuma impressora selecionada." };
      }
      return { ok: false, error: e?.message || "Falha ao abrir seletor de dispositivos." };
    }
    const result = await openUsbDevice(device);
    setConnecting(false);
    return result;
  }, [supported, openUsbDevice]);

  return {
    supported,
    connected,
    printerEverConnected,
    connecting,
    prefs,
    updatePrefs,
    connect,
    disconnect,
    printData,
    queue,
    enqueueOrder,
    clearQueue,
    flushQueue,
    processingQueue,
    printedOrderIds,
    refreshQueue,
    generateReceiptData: generateReceiptRef.current,
    setGenerateReceiptData,
    isAsciiOnlyPrinter: vendorId !== null && KNOWN_ASCII_ONLY_VIDS.has(vendorId),
  };
}
